import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useMyQueueUnread } from '../../hooks/useMyQueueUnread';
import {
  LayoutDashboard, Upload, Truck, Inbox, Shield, AlertTriangle,
  Users, Store, LogOut, Package, FileCheck, Activity, Settings as SettingsIcon,
  Search, BarChart3, ListChecks, Pause, IndianRupee, Gauge, LineChart, Sparkles,
} from 'lucide-react';

function LinkItem({ to, icon: Icon, children, end, badge }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition ${
          isActive ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'
        }`
      }
    >
      <Icon className="w-4 h-4" />
      <span className="flex-1">{children}</span>
      {badge != null && badge > 0 && (
        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-500 text-white min-w-[1.25rem] text-center">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </NavLink>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-4">
      <div className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">{title}</div>
      <nav className="space-y-1">{children}</nav>
    </div>
  );
}

export default function Sidebar() {
  const { user, logout, isAdmin, isSuperAdmin, isOps, isDispatchUser } = useAuth();
  const navigate = useNavigate();
  // D4: only Admin / User have a 'My Queue' link backed by the
  // dispatch_admin / dispatch_user team queue. Hook short-circuits silently
  // for other roles (returns 0).
  const { count: myQueueUnread } = useMyQueueUnread();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    // h-screen + shrink-0 so the sidebar is exactly viewport height and
    // never grows with a tall main pane. Middle nav area scrolls; header
    // + user/logout stay pinned top/bottom.
    <aside className="w-60 shrink-0 h-screen bg-white border-r border-gray-200 flex flex-col">
      <div className="px-4 py-5 border-b border-gray-200">
        <div className="text-lg font-semibold text-gray-900">Dispatch</div>
        <div className="text-xs text-gray-500">v2</div>
      </div>

      <div className="flex-1 px-3 py-4 overflow-y-auto">
        {/* D3 (Kalpana UAT round 3): Dispatch User sidebar exactly matches the
            requested layout — Dashboard, All Intake Batches, Search, Pending
            Orders, Scheduled Splits, Carrier Status. Admin / Ops keep the
            full nav. */}
        {isDispatchUser() && !isAdmin() && !isOps() ? (
          <>
            <Section title="Overview">
              <LinkItem to="/" icon={LayoutDashboard} end>Dashboard</LinkItem>
              <LinkItem to="/my-queue" icon={Inbox} badge={myQueueUnread}>My queue</LinkItem>
              <LinkItem to="/intake" icon={Inbox} end>All intake batches</LinkItem>
              <LinkItem to="/search" icon={Search}>Search orders</LinkItem>
            </Section>
            <Section title="Order flow">
              <LinkItem to="/pending-tracking" icon={Activity}>Pending orders</LinkItem>
              <LinkItem to="/hold-orders" icon={Pause}>Hold orders</LinkItem>
              <LinkItem to="/scheduled-splits" icon={Package}>Scheduled splits</LinkItem>
              <LinkItem to="/tracking/carrier" icon={Truck}>Carrier status</LinkItem>
            </Section>
            {/* Venky 2026-06-15: Dispatch Users record tracking + forward TN
                files to Admin, so they get these two chase boards too. */}
            <Section title="Tracking">
              <LinkItem to="/admin/vendor-followup" icon={Package}>Vendor follow-up</LinkItem>
              <LinkItem to="/admin/tracking-health" icon={Search}>Tracking health</LinkItem>
            </Section>
          </>
        ) : (
          <>
        <Section title="Overview">
          <LinkItem to="/" icon={LayoutDashboard} end>Dashboard</LinkItem>
          {/* My queue badge for Admin (or user when admin too) — Ops still
              uses /ops which has its own queue page. */}
          {isAdmin() && (
            <LinkItem to="/my-queue" icon={Inbox} badge={myQueueUnread}>My queue</LinkItem>
          )}
          {/* Global search, visible to admin + ops so they can jump to any
              order by external id / customer name without browsing batches */}
          {(isAdmin() || isOps()) && (
            <LinkItem to="/search" icon={Search}>Search orders</LinkItem>
          )}
        </Section>

        {(isAdmin() || isDispatchUser()) && (
          <Section title="Intake">
            {/* `end` so /intake doesn't also match /intake/upload and /intake/:id */}
            <LinkItem to="/intake" icon={Inbox} end>Intake batches</LinkItem>
            {isAdmin() && <LinkItem to="/intake/upload" icon={Upload}>Upload MBO</LinkItem>}
          </Section>
        )}

        {isAdmin() && (
          <>
            <Section title="Dispatch">
              <LinkItem to="/dispatch/ready" icon={Package}>Ready for dispatch</LinkItem>
              {/* `end` so /dispatch doesn't also match /dispatch/ready and /dispatch/:id */}
              <LinkItem to="/dispatch" icon={Truck} end>Dispatch batches</LinkItem>
            </Section>
            <Section title="Order flow">
              <LinkItem to="/pending-tracking" icon={Activity}>Pending orders</LinkItem>
              <LinkItem to="/hold-orders" icon={Pause}>Hold orders</LinkItem>
              <LinkItem to="/scheduled-splits" icon={Package}>Scheduled splits</LinkItem>
            </Section>
            <Section title="Tracking">
              <LinkItem to="/tracking/upload" icon={FileCheck}>Upload tracking</LinkItem>
              <LinkItem to="/tracking/share-with-ops" icon={Activity}>Share with Ops</LinkItem>
              <LinkItem to="/tracking/mbo-export" icon={Upload}>MBO export</LinkItem>
              <LinkItem to="/tracking/carrier" icon={Truck}>Carrier tracking</LinkItem>
            </Section>
          </>
        )}

        {isOps() && (
          <Section title="Ops">
            <LinkItem to="/ops/desk" icon={Gauge}>Ops desk</LinkItem>
            <LinkItem to="/ops" icon={Inbox} end>My queue</LinkItem>
            <LinkItem to="/ops/all" icon={Shield}>All orders</LinkItem>
            <LinkItem to="/admin/tracking-health" icon={Search}>Tracking health</LinkItem>
          </Section>
        )}

        {isAdmin() && (
          <Section title="Admin">
            <LinkItem to="/admin/data-quality" icon={AlertTriangle}>Data quality</LinkItem>
            <LinkItem to="/admin/validation-dashboard" icon={AlertTriangle}>Validation dashboard</LinkItem>
            <LinkItem to="/admin/dispatch-desk" icon={Gauge}>My desk</LinkItem>
            <LinkItem to="/admin/status-report" icon={BarChart3}>Status report</LinkItem>
            <LinkItem to="/admin/sales-report" icon={BarChart3}>Sales report</LinkItem>
            <LinkItem to="/admin/vendor-followup" icon={Package}>Vendor follow-up</LinkItem>
            <LinkItem to="/admin/tracking-health" icon={Search}>Tracking health</LinkItem>
            <LinkItem to="/ops/desk" icon={Truck}>Ops desk</LinkItem>
            <LinkItem to="/admin/product-mapping" icon={ListChecks}>Product mapping</LinkItem>
            <LinkItem to="/admin/cleanup" icon={ListChecks}>Data backfill</LinkItem>
            <LinkItem to="/admin/vendor-mapping" icon={ListChecks}>Vendor mapping</LinkItem>
            <LinkItem to="/admin/shipper-rules" icon={Truck}>Shipper rules</LinkItem>
            <LinkItem to="/admin/vendors" icon={Store}>Vendors</LinkItem>
            {isSuperAdmin() && <LinkItem to="/admin/users" icon={Users}>Users</LinkItem>}
          </Section>
        )}

        {isSuperAdmin() && (
          <Section title="Super admin">
            <LinkItem to="/admin/cockpit" icon={Gauge}>Owner cockpit</LinkItem>
            <LinkItem to="/admin/analytics" icon={LineChart}>Analytics</LinkItem>
            <LinkItem to="/admin/vendor-payables" icon={IndianRupee}>Vendor payables</LinkItem>
            <LinkItem to="/admin/payment-review" icon={FileCheck}>Payment review</LinkItem>
            <LinkItem to="/admin/money-at-risk" icon={AlertTriangle}>Money at risk</LinkItem>
            <LinkItem to="/admin/ai-chat" icon={Sparkles}>AI assistant</LinkItem>
            <LinkItem to="/admin/activity-logs" icon={Activity}>Activity log</LinkItem>
          </Section>
        )}
          </>
        )}
      </div>

      <div className="px-3 py-3 border-t border-gray-200">
        <div className="px-3 mb-2 text-xs text-gray-500 truncate">{user?.email}</div>
        <NavLink to="/settings"
                 className={({ isActive }) =>
                   `w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg mb-1 ${
                     isActive ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'
                   }`
                 }>
          <SettingsIcon className="w-4 h-4" /> Settings
        </NavLink>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-100"
        >
          <LogOut className="w-4 h-4" /> Log out
        </button>
      </div>
    </aside>
  );
}
